/**
 * Accent- and case-insensitive matching for chat search (#352 / #353).
 *
 * This is the JavaScript mirror of `BE/app/utils/text_normalize.py`. The two must
 * agree, because the user compares their numbers directly: the sidebar shows a
 * per-conversation occurrence count computed in SQL, and the in-chat bar shows
 * "N cases" for the same term in the same chat, computed here. Keep
 * `FOLD_SOURCE` byte-identical to the Python `_FOLD_SOURCE`.
 *
 * The shared rule, in one sentence: a match is a NON-OVERLAPPING, case-insensitive,
 * accent-folded occurrence of the query, matched as a LITERAL string.
 *
 * Like the backend, we expand the NEEDLE into the set of characters that fold onto
 * it ("papa" -> /p[aàáâãäåāăą]p[aàáâãäåāăą]/i) rather than folding the haystack.
 * Here the motive is correctness rather than speed: folding the rendered text would
 * shift every character offset, and the highlighter needs to slice the ORIGINAL
 * string so the marks land on the characters the user is actually looking at.
 */

// Must match BE `_FOLD_SOURCE`.
const FOLD_SOURCE =
    'àáâãäåāăąèéêëēĕėęěìíîïĩīĭįòóôõöōŏőùúûüũūŭůűųñńņňçćčĉċ' +
    'ýÿŷśšşŝșžźżřŗ';

/** Lowercased, diacritic-free form — the JS twin of Python `strip_accents`. */
export const stripAccents = (text) => {
    if (!text) return '';
    // NFKD splits "á" into "a" + a combining mark; \p{M} drops the marks.
    return text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
};

// base letter -> every character that folds onto it.
const ACCENT_VARIANTS = (() => {
    const map = new Map();
    for (const ch of FOLD_SOURCE) {
        const base = stripAccents(ch);
        // Only clean single-ASCII-letter foldings, exactly as the BE filters them.
        if (base.length !== 1 || base.charCodeAt(0) > 127) continue;
        if (!map.has(base)) map.set(base, []);
        const bucket = map.get(base);
        bucket.push(ch);
        const upper = ch.toUpperCase();
        if (upper.length === 1 && upper !== ch) bucket.push(upper);
    }
    return map;
})();

const RE_META = new Set('.[]()*+?{}|^$\\/-'.split(''));

/**
 * Build a RegExp matching `query` accent- and case-insensitively.
 *
 * `flags` defaults to 'gi' (global, for counting/splitting). The query is always
 * matched LITERALLY — regex metacharacters a user types are escaped, so typing
 * "a.c" searches for "a.c" and not "abc".
 *
 * Returns null for an empty query; callers must treat that as "no search" rather
 * than building an empty regex (which matches everywhere).
 */
export const buildFoldRegex = (query, flags = 'gi') => {
    const folded = stripAccents(query || '');
    if (!folded) return null;
    let out = '';
    for (const ch of folded) {
        const variants = ACCENT_VARIANTS.get(ch);
        if (variants) {
            out += `[${ch}${ch.toUpperCase()}${variants.join('')}]`;
        } else if (RE_META.has(ch)) {
            out += `\\${ch}`;
        } else {
            out += ch;
        }
    }
    return new RegExp(out, flags);
};

/**
 * Count NON-OVERLAPPING accent/case-insensitive occurrences of `query` in `text`.
 *
 * The mirror of Postgres `regexp_count(content, <pattern>, 1, 'i')`. Non-overlapping
 * is what both a regex scan and a human count give: "aaa" contains ONE "aa".
 */
export const countOccurrences = (text, query) => {
    const re = buildFoldRegex(query);
    if (!re || !text) return 0;
    return (text.match(re) || []).length;
};

/**
 * Split `text` into alternating [plain, match, plain, match, ...] segments.
 *
 * Used by the highlighter: a capture group in `split` keeps the matched text, and
 * because the pattern is built from the ORIGINAL characters (not a folded copy)
 * every segment is a verbatim slice of the input — the highlight lands exactly on
 * the characters the user sees, accents and casing intact.
 *
 * Returns null when there is nothing to highlight, so a caller can cheaply keep
 * its original children rather than rebuilding an identical tree.
 */
export const splitByMatches = (text, query) => {
    const re = buildFoldRegex(query);
    if (!re || !text) return null;
    re.lastIndex = 0;
    if (!re.test(text)) return null;
    const capture = new RegExp(`(${re.source})`, re.flags);
    return text.split(capture);
};
